import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid-premium";
import GridExportMenu from "../common/GridExportMenu";
import { Box, Typography, Button, Divider, Chip, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PaidIcon from "@mui/icons-material/Paid";
import ClearIcon from "@mui/icons-material/Clear";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  apiRef: any;

  title: string;
  total: number;
  selectedCount: number;

  onApprove: () => void;
  onReject: () => void;
  onAddInvoice: () => void;
  onFetchFromKsef: () => void;

  ksefLoading?: boolean;

  onBulkPaymentStatus: () => void;

  onClearSelection?: () => void;
  hasPostedSelected?: boolean;
};

export default function DocumentsToolbar({
  apiRef,
  title,
  total,
  selectedCount,
  onApprove,
  onReject,
  onAddInvoice,
  onFetchFromKsef,
  ksefLoading = false,
  onBulkPaymentStatus,
  onClearSelection,
  hasPostedSelected = false,
}: Props) {
  const { t } = useAppTranslation("common");

  return (
    <GridToolbarContainer sx={{ px: 3, py: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" fontWeight={500}>
          {title}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {total} {t("dokumentów")}
          {selectedCount > 0 && ` • ${selectedCount} ${t("zaznaczonych")}`}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddInvoice}
        >
          {t("Dodaj fakturę")}
        </Button>

        <Button
          variant="text"
          startIcon={<CloudDownloadIcon />}
          onClick={onFetchFromKsef}
          disabled={ksefLoading}
        >
          {ksefLoading ? t("Pobieranie...") : t("Pobierz z KSeF")}
        </Button>

        <Divider orientation="vertical" flexItem />

        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />

        <Divider orientation="vertical" flexItem />

        <GridExportMenu
          label="Eksport"
          fileName={`faktury_${new Date().toISOString().slice(0, 10)}`}
          excelOptions={{
            columnsStyles: {
              documentDate: { numFmt: "dd.mm.yyyy" },
              dueDate: { numFmt: "dd.mm.yyyy" },
              amountGross: { numFmt: "#,##0.00" },
            },
          }}
        />
      </Box>

      {selectedCount > 0 && (
        <Box
          sx={{
            width: "100%",
            mt: 2,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.default",
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
            }}
          >
            <Typography fontWeight={700}>{t("Tryb masowy")}</Typography>

            <Chip
              label={`${selectedCount} ${t("zaznaczonych")}`}
              size="small"
              color="primary"
              variant="outlined"
            />

            {hasPostedSelected && (
              <Tooltip
                title={t(
                  "Wśród zaznaczonych dokumentów znajdują się zaksięgowane (Posted). Niektóre operacje są zablokowane."
                )}
                arrow
              >
                <WarningAmberIcon
                  fontSize="small"
                  sx={{ color: "warning.main", ml: 0.5 }}
                />
              </Tooltip>
            )}

            {onClearSelection && (
              <Button
                size="small"
                variant="text"
                startIcon={<ClearIcon />}
                onClick={onClearSelection}
                sx={{ textTransform: "none" }}
              >
                {t("Wyczyść")}
              </Button>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: { xs: "flex-end", md: "flex-end" },
              flexWrap: "wrap",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<PaidIcon />}
              onClick={onBulkPaymentStatus}
              sx={{ textTransform: "none" }}
            >
              {t("Status płatności")}
            </Button>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: "none", md: "block" }, mx: 0.5 }}
            />

            <Tooltip
              title={
                hasPostedSelected
                  ? t("Nie można zmienić statusu – wybrane dokumenty są zaksięgowane.")
                  : ""
              }
            >
              <span>
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={onReject}
                  disabled={hasPostedSelected}
                  sx={{ textTransform: "none" }}
                >
                  {t("Odrzuć")}
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                hasPostedSelected
                  ? t("Nie można zmienić statusu – wybrane dokumenty są zaksięgowane.")
                  : ""
              }
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={onApprove}
                  disabled={hasPostedSelected}
                  sx={{ textTransform: "none" }}
                >
                  {t("Akceptuj")}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}
    </GridToolbarContainer>
  );
}